import type { Rec, Finding, PicRow, TrendPoint, WorkItem } from "@shared/types.js";
import { computeStats } from "@shared/stats.js";
import { fpct } from "./format.js";
import { COMP_ORDER, COMP_LABEL, CATS, compRows } from "./comps.js";
import { RULE_TITLE, RULE_DESC } from "./rules.js";

export type Cell = string | number | null | undefined;

export interface Sheet {
  headers: string[];
  rows: Cell[][];
  name: string;
}

function csvEscape(v: Cell, sep: string): string {
  const s = v === null || v === undefined ? "" : String(v);
  if (sep === "\t") return s.replace(/[\t\r\n]/g, " ");
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toDelim(sheet: Sheet, sep: string): string {
  const out = [sheet.headers.map((h) => csvEscape(h, sep)).join(sep)];
  for (const r of sheet.rows) out.push(r.map((c) => csvEscape(c, sep)).join(sep));
  return out.join("\r\n");
}

export function derivedStatus(r: Rec): string {
  if (r.nrDone) return "Priced (new rate)";
  if (r.estDone) return "Estimated";
  if (r.received) return "Received – awaiting pricing";
  return "Outstanding – CR not received";
}

export function recordExport(rows: Rec[]): Sheet {
  return {
    name: "rate-27-28-records",
    headers: [
      "Component", "Sheet", "Excel row", "Code", "Supplier", "Product", "Town", "Region",
      "Category", "CR status (raw)", "CR received", "CR received date", "New rate status (raw)",
      "New rate done", "New rate input date", "PIC new rate", "Estimation status (raw)",
      "Estimation done", "Estimation input date", "PIC estimation", "Derived status", "Rate type",
      "Selling decision", "Crosschecked by", "Crosscheck date", "Upload date", "Export date",
      "Cancellation policy info",
    ],
    rows: rows.map((r) => [
      r.compLabel, r.sheet, r.excelRow, r.code, r.name, r.product, r.town, r.region, r.cat,
      r.recvRaw, r.received ? "Yes" : "No", r.recvDate, r.nrRaw, r.nrDone ? "Yes" : "No", r.nrDate,
      r.nrPic, r.estRaw, r.estDone ? "Yes" : "No", r.estDate, r.estPic, derivedStatus(r), r.rateType,
      r.selling, r.ccBy, r.ccDate, r.upDate, r.exDate, r.cancel,
    ]),
  };
}

export function findingExport(findings: Finding[]): Sheet {
  return {
    name: "rate-27-28-crosscheck",
    headers: [
      "Severity", "Check", "Issue", "Detail", "Component", "Sheet", "Excel row",
      "Code", "Supplier", "Product", "Category", "Region",
    ],
    rows: findings.map((f) => [
      f.severity, RULE_TITLE[f.rule] ?? f.rule, f.message, RULE_DESC[f.rule] ?? "",
      f.compLabel, f.sheet, f.excelRow, f.code, f.name, f.product, f.cat, f.region,
    ]),
  };
}

export function summaryExport(all: Rec[]): Sheet {
  const rows: Cell[][] = [];
  const line = (scope: string, label: string, set: Rec[]) => {
    const s = computeStats(set);
    rows.push([
      scope, label, s.total, s.received, fpct(s.received, s.total), s.priced,
      fpct(s.received - s.awaiting, s.received), s.awaiting, s.notReceived, s.estimated,
      fpct(s.notReceived - s.estOutstanding, s.notReceived), s.estOutstanding, s.crosschecked,
      s.exported, `${s.completion.toFixed(1)}%`,
    ]);
  };

  line("All", "Total in scope", all);
  for (const c of COMP_ORDER) {
    const set = compRows(all, c);
    if (!set.length) continue;
    line("Component", COMP_LABEL[c], set);
    for (const cat of CATS) {
      const cs = set.filter((r) => r.cat === cat);
      if (cs.length) line(COMP_LABEL[c], `Category ${cat}`, cs);
    }
    const nc = set.filter((r) => !r.cat);
    if (nc.length) line(COMP_LABEL[c], "No category", nc);
  }

  return {
    name: "rate-27-28-summary",
    headers: [
      "Scope", "Breakdown", "Records", "CR received", "% received", "Priced",
      "% priced of received", "Awaiting pricing", "CR not received", "Estimated",
      "% estimated of not received", "Estimation outstanding", "Crosschecked", "Exported",
      "% complete",
    ],
    rows,
  };
}

export function picExport(pic: PicRow[]): Sheet {
  return {
    name: "rate-27-28-pic",
    headers: [
      "PIC", "New rate entries", "Estimation entries", "Total entered", "Crosschecked",
      "First entry", "Last entry", "Active days", "Entries per active day",
    ],
    rows: pic.map((p) => [
      p.pic, p.newRate, p.estimation, p.entered, p.crosschecked,
      p.firstEntry, p.lastEntry, p.activeDays, p.perDay.toFixed(2),
    ]),
  };
}

export function worklistExport(items: WorkItem[]): Sheet {
  return {
    name: "rate-27-28-worklist",
    headers: [
      "Priority", "Action", "Why", "Supplier", "Code", "Component", "Region", "Town",
      "Category", "Last touched by", "Waiting since", "Days waiting", "Sheet", "Excel row",
    ],
    rows: items.map((i) => [
      i.priority, i.action, i.why, i.name, i.code, i.compLabel, i.region, i.town,
      i.cat, i.owner, i.waitingSince, i.ageDays, i.sheet, i.excelRow,
    ]),
  };
}

export function trendExport(points: TrendPoint[]): Sheet {
  return {
    name: "rate-27-28-trend",
    headers: [
      "Snapshot", "Label", "Generated at", "Source file", "Records", "CR received",
      "Priced", "Estimated", "Entered", "Awaiting pricing", "Crosschecked", "Exported",
      "% complete",
    ],
    rows: points.map((p) => [
      p.snapshotId, p.label, p.generatedAt, p.sourceFile, p.stats.total, p.stats.received,
      p.stats.priced, p.stats.estimated, p.stats.entered, p.stats.awaiting,
      p.stats.crosschecked, p.stats.exported, `${p.stats.completion.toFixed(1)}%`,
    ]),
  };
}

/** Trigger a browser download of one sheet as CSV. */
export function downloadCsv(sheet: Sheet): void {
  const text = toDelim(sheet, ",");
  const blob = new Blob(["﻿", text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${sheet.name}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Copy one sheet as TSV, which pastes straight into Excel. */
export async function copyForExcel(sheet: Sheet): Promise<number> {
  const text = toDelim(sheet, "\t");
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    ta.remove();
  }
  return sheet.rows.length;
}
